export const APP_TITLE = 'RudderStack';

/** Between the page description and the app name: `Sources | RudderStack`. */
export const TITLE_SEPARATOR = ' | ';

/** Between the parts of the page description: `Connect Destination - Acme - Sources`. */
export const DETAIL_SEPARATOR = ' - ';

/**
 * Between the two ends of a flow: `Acme Webhook → Snowflake`. A real arrow, not
 * `->` — this string is shown in page headers as well as the tab title.
 */
const FLOW_ARROW = '→';

/** Names a page that is about a flow from one resource to another. */
export const formatResourceFlow = (from?: string, to?: string): string | undefined => {
  if (!from || !to) {
    return from || to;
  }

  return `${from} ${FLOW_ARROW} ${to}`;
};

/**
 * Section names, declared on route `handle` metadata.
 *
 * Title Case throughout, except where a name reads as a settings page
 * ("Workspace settings") — those wordings are fixed by product.
 */
export const SECTION = {
  accessManagement: 'Access Management',
  acceptInvite: 'Accept Invite',
  /** Sub-sections of Access Management, which are `?tab=` views, not routes. */
  groups: 'Groups',
  members: 'Members',
  serviceAccessTokens: 'Service Access Tokens',
  accountLinking: 'Account Linking',
  accountSetup: 'Account setup',
  audiences: 'rETL Audiences',
  authorize: 'Authorize',
  botManagement: 'Bot Management',
  cohortAudiences: 'Cohort Audiences',
  connections: 'Connections',
  dataCatalog: 'Data Catalog',
  dataGraph: 'Data Graph',
  dataGraphAudiences: 'Data Graph Audiences',
  /** The sync detail page under a data graph audience is its own section. */
  dataGraphAudienceSyncs: 'Data graph audience syncs',
  dataMapper: 'Data Mapper',
  destinations: 'Destinations',
  directory: 'Directory',
  eventBlocking: 'Event Blocking',
  feedback: 'Feedback',
  forgotPassword: 'Forgot Password',
  getStarted: 'Get Started',
  health: 'Health',
  healthDashboard: 'Health Dashboard',
  invoices: 'Invoices',
  libraries: 'Libraries',
  logIn: 'Log In',
  models: 'rETL Models',
  multiFactorAuthentication: 'Multi-Factor Authentication',
  organizationSettings: 'Organization settings',
  phoneVerification: 'Phone Verification',
  profileSettings: 'Profile settings',
  profiles: 'Profiles',
  retlConnections: 'rETL Connections',
  selectOrganization: 'Select Organization',
  signUp: 'Sign Up',
  singleSignOn: 'Single Sign-On',
  sources: 'Sources',
  trackingPlans: 'Tracking Plans',
  transformations: 'Transformations',
  trialExpired: 'Trial Expired',
  userSuspended: 'User Suspended',
  warehouseDestinationSyncs: 'Warehouse destination syncs',
  workspaceSettings: 'Workspace settings',
} as const;

/**
 * Actions performed on an entity within a section. These prefix the entity name:
 * `Connect Destination - Acme - Sources`.
 */
export const ACTION = {
  /** Every create/setup page: the entity name that follows says what is created. */
  create: 'Create',
  cloneDestination: 'Clone Destination',
  connectDestination: 'Connect Destination',
  connectSource: 'Connect Source',
  destinationMapping: 'Destination Mapping',
  linkSource: 'Link Source',
  linkTrackingPlan: 'Link Tracking Plan',
  liveEvents: 'Live Events',
  syncs: 'Syncs',
} as const;
